import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../components/ui/Header';
import Button from '../components/ui/Button';
import Icon from '../components/AppIcon';

const NotFound = () => {
  useEffect(() => {
    document.title = "404 - Page Not Found | Musa Yusuf Magoban";
  }, []);

  const quickLinks = [
    { name: 'Home', path: '/', icon: 'Home', description: 'Return to homepage' },
    { name: 'Projects', path: '/projects', icon: 'FolderOpen', description: 'View my work' },
    { name: 'About', path: '/about', icon: 'User', description: 'Learn more about me' },
    { name: 'Contact', path: '/contact', icon: 'Mail', description: 'Get in touch' }
  ];

  const handleNavigation = (path) => {
    window.location.href = path;
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>404 - Page Not Found | Musa Yusuf Magoban</title>
        <meta name="description" content="The page you're looking for doesn't exist. Navigate back to explore my cybersecurity portfolio." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <Header />

      <main className="pt-16">
        {/* 404 Hero Section */}
        <section className="py-20 bg-gradient-to-br from-primary/10 via-secondary/5 to-accent/5 relative overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
            <div className="space-y-8">
              {/* 404 Visual */}
              <div className="relative">
                <div className="text-8xl sm:text-9xl font-bold text-primary/10 select-none">
                  404
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-24 h-24 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-full flex items-center justify-center animate-pulse">
                    <Icon name="AlertTriangle" size={48} className="text-primary" />
                  </div>
                </div>
              </div>

              {/* Error Message */}
              <div className="space-y-4">
                <h1 className="text-4xl sm:text-5xl font-bold text-foreground">
                  Page Not <span className="text-primary">Found</span>
                </h1>
                <p className="text-xl text-text-secondary max-w-2xl mx-auto">
                  The page you're looking for seems to have vanished into the digital void. 
                  But don't worry - let's get you back on track.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="default"
                  size="lg"
                  iconName="Home"
                  iconPosition="left"
                  onClick={() => handleNavigation('/')}
                  className="transform hover:-translate-y-1 transition-all duration-200"
                >
                  Back to Home
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  iconName="RotateCcw"
                  iconPosition="left"
                  onClick={() => window.history.back()}
                  className="transform hover:-translate-y-1 transition-all duration-200"
                >
                  Go Back
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Navigation */}
        <section className="py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="inline-flex items-center px-4 py-2 bg-secondary/10 rounded-full text-secondary font-medium text-sm mb-6">
                <Icon name="Navigation" size={16} className="mr-2" />
                Quick Navigation
              </div>
              <h2 className="text-3xl font-bold text-foreground mb-4">
                Explore My Portfolio
              </h2>
              <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                While you're here, check out these sections to learn more about my cybersecurity journey
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {quickLinks.map((link) => (
                <div
                  key={link.name}
                  onClick={() => handleNavigation(link.path)}
                  className="group bg-surface rounded-xl p-6 security-shadow hover:security-shadow-strong transition-all duration-300 transform hover:-translate-y-1 border border-border/50 hover:border-primary/20 cursor-pointer relative overflow-hidden"
                >
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                  
                  <div className="relative z-10 text-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                      <Icon name={link.icon} size={24} className="text-primary group-hover:text-secondary transition-colors duration-300" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors duration-300">
                      {link.name}
                    </h3>
                    <p className="text-text-secondary text-sm leading-relaxed">
                      {link.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Search Suggestions */}
        <section className="py-20 bg-gradient-to-br from-muted/30 to-muted/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-4">
                Looking for Something Specific?
              </h2>
              <p className="text-lg text-text-secondary">
                Here are some popular sections visitors often explore
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-surface rounded-xl p-6 security-shadow text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Icon name="Shield" size={24} className="text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Security Projects</h3>
                <p className="text-text-secondary text-sm mb-4">
                  Explore my hands-on cybersecurity projects and SOC lab implementations
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleNavigation('/projects')}
                  className="w-full"
                >
                  View Projects
                </Button>
              </div>

              <div className="bg-surface rounded-xl p-6 security-shadow text-center">
                <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Icon name="Award" size={24} className="text-secondary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Certifications</h3>
                <p className="text-text-secondary text-sm mb-4">
                  View my industry-recognized cybersecurity certifications and achievements
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleNavigation('/certifications')}
                  className="w-full"
                >
                  View Certifications
                </Button>
              </div>

              <div className="bg-surface rounded-xl p-6 security-shadow text-center">
                <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Icon name="Code" size={24} className="text-accent" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Technical Skills</h3>
                <p className="text-text-secondary text-sm mb-4">
                  Discover my technical expertise in SOC operations and security tools
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleNavigation('/skills')}
                  className="w-full"
                >
                  View Skills
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-20 bg-gradient-to-br from-primary to-secondary relative overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-3xl lg:text-4xl font-bold text-white">
                  Still Can't Find What You're Looking For?
                </h2>
                <p className="text-xl text-white/90 max-w-2xl mx-auto">
                  I'd be happy to help you find the information you need or discuss any questions about my work.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => handleNavigation('/contact')}
                  className="bg-white text-primary px-8 py-3 rounded-lg font-medium hover:bg-white/90 transition-all duration-200 transform hover:-translate-y-1 shadow-lg hover:shadow-xl"
                >
                  <Icon name="Mail" size={18} className="mr-2 inline" />
                  Get In Touch
                </button>
                <button
                  onClick={() => handleNavigation('/about')}
                  className="border-2 border-white/30 text-white px-8 py-3 rounded-lg font-medium hover:bg-white/10 transition-all duration-200"
                >
                  <Icon name="User" size={18} className="mr-2 inline" />
                  Learn More About Me
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default NotFound;
